/**
 * Which of a client's assignments is the one running now, and what a card needs to know about it.
 *
 * `Active` is the backend's word and the only one that counts: training-plan R14 lets a client hold
 * at most one active assignment, so the first match is the answer. A client with none answers
 * `null` — a paused, completed or cancelled plan is not "the plan", and a card that drew one as
 * running would offer writes the server refuses.
 *
 * Pure: no React, no DOM, no query hook.
 */

import type { TrainWithMeModulesTrainingDtosResponsesPlanAssignmentSummary as PlanAssignmentSummary } from '~/shared/api/generated/model';

export interface AssignmentFacts {
  readonly id: number;
  readonly name: string | null;
  /** The plan's category as the wire spells it (`Strength`), or `null`. */
  readonly category: string | null;
  readonly coverUrl: string | null;
  /** `Calendar` or `SelfPaced` as the wire spells it, or `null`. */
  readonly schedulingMode: string | null;
}

function text(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed === undefined || trimmed === '' ? null : trimmed;
}

function assignmentFacts(summary: PlanAssignmentSummary): AssignmentFacts | null {
  if (typeof summary.id !== 'number') return null;
  return {
    id: summary.id,
    name: text(summary.templateName),
    category: text(summary.category),
    coverUrl: text(summary.coverImageUrl),
    schedulingMode: text(summary.schedulingMode),
  };
}

/** The client's running assignment, or `null` where none is `Active`. */
export function activeAssignment(
  assignments: readonly PlanAssignmentSummary[] | null | undefined,
): AssignmentFacts | null {
  const running = (assignments ?? []).find((entry) => entry.status === 'Active');
  return running === undefined ? null : assignmentFacts(running);
}

/** One assignment by id, whatever its status — a card about a finished plan still names it. */
export function assignmentById(
  assignments: readonly PlanAssignmentSummary[] | null | undefined,
  id: number,
): AssignmentFacts | null {
  const found = (assignments ?? []).find((entry) => entry.id === id);
  return found === undefined ? null : assignmentFacts(found);
}
